// TanStack Query hooks for the SP1 Benchmarks feature (Slices 5 + 6).
//
// Public read endpoints under `/benchmarks/*` and admin write endpoints under
// `/admin/benchmarks/*` are documented in the Slice 2–4 PRs (#265, #268, #269).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, API_BASE_URL, buildQueryString } from '@/lib/api'

// ─── Shared Benchmark type ────────────────────────────────────────────────────

export type BenchmarkStatus = 'draft' | 'published' | 'suppressed'

export type BenchmarkArchiveStatus =
  | 'pending'
  | 'archived'
  | 'link_only'
  | 'failed'

export type BenchmarkSourceKind = 'manual' | 'rss_auto' | 'analyst_auto' | 'api_auto'

export interface Benchmark {
  id: string
  title: string
  publisher: string
  publisherUrl: string | null
  publisherType: string
  reportType: string
  topics: string[]
  reportUrl: string
  keyMetric: string
  description: string
  publishedAt: string
  pdfObjectKey: string | null
  pdfSizeBytes: number | null
  pdfContentType: string | null
  status: BenchmarkStatus
  sourceKind: BenchmarkSourceKind
  createdByEmail: string | null
  updatedByEmail: string | null
  suppressedByEmail: string | null
  suppressedAt: string | null
  createdAt: string
  updatedAt: string
  // SP2 + SP4 Wave 1 — present on rows inserted by the benchmark-rss
  // adapter. SP1-era rows have these as `undefined` (missing fields),
  // hence the `?` optional markers rather than `| null`.
  archiveStatus?: BenchmarkArchiveStatus
  archivedPdfUrl?: string | null
  publisherSlug?: string | null
  draftSummary?: string | null
  // KAN-822 T6 — the candidate's disposition version (0125); the routing API
  // requires it as `expectedVersion`. Optional for rows served before 0125.
  dispositionVersion?: number
  // KAN-822 T9 — the latest route generation's state; null when never routed.
  // Served by the admin list only (absent on the public API and older rows).
  routeState?: RouteState | null
}

export interface BenchmarkList {
  items: Benchmark[]
  total: number
}

// ─── Public read hooks (CISO surface) ─────────────────────────────────────────

export interface BenchmarkPublicFilters {
  publisherType?: string
  reportType?: string
  topic?: string
  /**
   * Wave-1 A1e — feed-specific filter. The RSS adapter writes
   * `benchmarks.publisher_slug` on insert; SP1-era rows can be NULL and
   * drop out when this filter is supplied (desired behaviour).
   */
  publisherSlug?: string
  limit?: number
  offset?: number
}

export function useBenchmarksList(filters: BenchmarkPublicFilters = {}) {
  return useQuery<BenchmarkList>({
    queryKey: ['benchmarks', filters],
    queryFn: () =>
      api.get<BenchmarkList>(`/benchmarks${buildQueryString({ ...filters })}`),
    staleTime: 60_000,
  })
}

export function useBenchmark(id: string | undefined) {
  return useQuery<Benchmark>({
    queryKey: ['benchmarks', id],
    queryFn: () => api.get<Benchmark>(`/benchmarks/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  })
}

export function useBenchmarkPdfUrl(id: string | undefined, enabled: boolean) {
  return useQuery<{ url: string; expiresAt: string }>({
    queryKey: ['benchmark-pdf-url', id],
    queryFn: () => api.get<{ url: string; expiresAt: string }>(`/benchmarks/${id}/pdf-url`),
    enabled: !!id && enabled,
    // Keep it shorter than the 1h presign expiry so auto-refetches get fresh URLs.
    staleTime: 30_000,
  })
}

// ─── Admin read hooks ─────────────────────────────────────────────────────────

export interface BenchmarkAdminFilters {
  status?: string
  /** KAN-822 T9 — the routed history view: candidates with routing history. */
  routed?: 'true'
  limit?: number
  offset?: number
}

export function useAdminBenchmarks(filters: BenchmarkAdminFilters = {}) {
  return useQuery<BenchmarkList>({
    queryKey: ['admin-benchmarks', filters],
    queryFn: () =>
      api.get<BenchmarkList>(
        `/admin/benchmarks${buildQueryString({ ...filters })}`,
      ),
    staleTime: 10_000,
  })
}

// ─── SP2 + SP4 Wave 1 — pending queue hooks ──────────────────────────────────

/**
 * SP3 Wave 2 publisher-type filter for the pending queue.
 *
 * Values mirror the DB `publisher_type` pg enum. `undefined` means "all" and
 * omits the query-param so SP2 + SP1 rows surface unfiltered.
 */
export type PublisherTypeFilter =
  | 'analyst'
  | 'vendor'
  | 'research_firm'
  | 'consortium'
  | 'government_agency'
  | 'other'

/**
 * SP2 pending queue — filters the admin list on
 * `status=draft AND source_kind != 'manual'`. The server side enforces
 * the filter; this hook just bolts the params together.
 * (Migration 0024 replaced the old `createdByEmail LIKE 'system:%'` pattern
 * with the `source_kind` enum column; `sourceKind=auto` is the convenience alias.)
 *
 * SP3 Wave 2 added `publisherType` for the filter-chip row on the pending
 * page. When omitted the list returns all pending rows regardless of type.
 */
export function useBenchmarksPending(
  limit = 50,
  offset = 0,
  publisherType?: PublisherTypeFilter,
) {
  return useQuery<BenchmarkList>({
    queryKey: ['admin-benchmarks', 'pending', { limit, offset, publisherType }],
    queryFn: () =>
      api.get<BenchmarkList>(
        `/admin/benchmarks${buildQueryString({
          status: 'draft',
          sourceKind: 'auto',
          publisherType,
          limit,
          offset,
        })}`,
      ),
    staleTime: 10_000,
  })
}

/**
 * Sidebar badge count — cheap `SELECT count(*)` against the same filter.
 * Refetches every 60s so new SP2 ingests surface without a reload.
 *
 * SP3 Wave 2 added an optional `publisherType` filter so the filter-chip row
 * can show per-type badges (All / Vendor / Analyst / …).
 */
export function useAdminBenchmarksPendingCount(
  publisherType?: PublisherTypeFilter,
) {
  return useQuery<{ count: number }>({
    queryKey: ['admin-benchmarks', 'pending-count', { publisherType }],
    refetchInterval: 60_000,
    queryFn: () =>
      api.get<{ count: number }>(
        `/admin/benchmarks/pending-count${buildQueryString({
          publisherType,
        })}`,
      ),
  })
}

export function useAdminBenchmark(id: string | undefined) {
  return useQuery<Benchmark>({
    queryKey: ['admin-benchmarks', id],
    queryFn: () => api.get<Benchmark>(`/admin/benchmarks/${id}`),
    enabled: !!id,
    staleTime: 10_000,
  })
}

// ─── Admin write hooks ────────────────────────────────────────────────────────

export interface CreateBenchmarkBody {
  title: string
  publisher: string
  publisherUrl: string | null
  publisherType: string
  reportType: string
  topics: string[]
  reportUrl: string
  keyMetric: string
  description: string
  publishedAt: string
}

export type UpdateBenchmarkBody = Partial<CreateBenchmarkBody>

export function useCreateBenchmark() {
  const qc = useQueryClient()
  return useMutation<Benchmark, Error, CreateBenchmarkBody>({
    mutationFn: (body) => api.post<Benchmark>('/admin/benchmarks', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
      qc.invalidateQueries({ queryKey: ['benchmarks'] })
    },
  })
}

export function useUpdateBenchmark(id: string) {
  const qc = useQueryClient()
  return useMutation<Benchmark, Error, UpdateBenchmarkBody>({
    mutationFn: (body) => api.patch<Benchmark>(`/admin/benchmarks/${id}`, body),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
      qc.invalidateQueries({ queryKey: ['benchmarks'] })
      qc.setQueryData(['admin-benchmarks', id], row)
    },
  })
}

export function usePublishBenchmark(id: string) {
  const qc = useQueryClient()
  return useMutation<Benchmark, Error, void>({
    mutationFn: () => api.post<Benchmark>(`/admin/benchmarks/${id}/publish`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
      qc.invalidateQueries({ queryKey: ['benchmarks'] })
    },
  })
}

export function useSuppressBenchmark(id: string) {
  const qc = useQueryClient()
  return useMutation<Benchmark, Error, { reason?: string } | void>({
    mutationFn: (body) => api.post<Benchmark>(`/admin/benchmarks/${id}/suppress`, body ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
      qc.invalidateQueries({ queryKey: ['benchmarks'] })
    },
  })
}

// KAN-822 T6 — Route to Vendor stories. Captain Q61: the operator may pick the
// Vendor update subtype (a canonical `vendor-update-subtype` slug) or null.
export interface RouteToVendorBody {
  expectedVersion: number
  requestId: string
  vendorUpdateSubtype: string | null
}

export const ROUTE_STATES = ['queued', 'processing', 'published', 'review', 'failed'] as const
export type RouteState = (typeof ROUTE_STATES)[number]

export interface RouteReceipt {
  routeId: string
  benchmarkId: string
  state: RouteState
  articleId: string | null
  storyId: string | null
  reasonCode: string | null
  retryable: boolean
  version: number
}

export function useRouteBenchmarkToVendor(id: string) {
  const qc = useQueryClient()
  return useMutation<RouteReceipt, Error, RouteToVendorBody>({
    mutationFn: (body) => api.post<RouteReceipt>(`/admin/benchmarks/${id}/route-to-vendor`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
    },
  })
}

// ─── KAN-822 T9 — the operator's view of a routing obligation ────────────────

const storyRouteKey = (id: string | undefined) => ['admin-benchmarks', id, 'story-route'] as const

/** How often to re-read a route: only while the pipeline is working on it. */
export const ROUTE_POLL_MS = 5_000
export function routePollInterval(receipt: RouteReceipt | null | undefined): number | false {
  return receipt?.state === 'queued' || receipt?.state === 'processing' ? ROUTE_POLL_MS : false
}

/** A value the server sent as a route receipt, checked before it is cached. */
function asReceipt(value: unknown): RouteReceipt | null {
  if (!value || typeof value !== 'object') return null
  const r = value as Record<string, unknown>
  const nullableString = (v: unknown) => v === null || typeof v === 'string'
  return typeof r.routeId === 'string' &&
    typeof r.benchmarkId === 'string' &&
    typeof r.state === 'string' &&
    (ROUTE_STATES as readonly string[]).includes(r.state) &&
    nullableString(r.articleId) &&
    nullableString(r.storyId) &&
    nullableString(r.reasonCode) &&
    typeof r.retryable === 'boolean' &&
    typeof r.version === 'number'
    ? (r as unknown as RouteReceipt)
    : null
}

/** The `current` receipt a 409 carries, or null when the body holds none. */
function currentReceiptFrom(body: string): RouteReceipt | null {
  try {
    return asReceipt((JSON.parse(body) as { current?: unknown } | null)?.current)
  } catch {
    return null
  }
}

/**
 * The candidate's route receipt, read from the server so it survives a reload
 * (R3). A 404 means "never routed" and is `null`, not an error; any other
 * failure stays an error. Polled only while queued or processing.
 */
export function useBenchmarkStoryRoute(id: string | undefined) {
  return useQuery<RouteReceipt | null>({
    queryKey: storyRouteKey(id),
    queryFn: async () => {
      try {
        return await api.get<RouteReceipt>(`/admin/benchmarks/${id}/story-route`)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null
        throw err
      }
    },
    enabled: !!id,
    refetchInterval: (query) => routePollInterval(query.state.data),
  })
}

/**
 * Retry a failed, retryable route under the same identity (R16). The receipt's
 * version is the conflict token (R17): on a 409 the cached receipt becomes the
 * server's current one, so the operator sees the state that won.
 */
export function useRetryBenchmarkRoute(id: string) {
  const qc = useQueryClient()
  return useMutation<RouteReceipt, Error, { expectedRouteVersion: number }>({
    mutationFn: (body) => api.post<RouteReceipt>(`/admin/benchmarks/${id}/story-route/retry`, body),
    onSuccess: (receipt) => {
      qc.setQueryData(storyRouteKey(id), receipt)
    },
    onError: (err) => {
      if (!(err instanceof ApiError) || err.status !== 409) return
      const current = currentReceiptFrom(err.body)
      if (current) qc.setQueryData(storyRouteKey(id), current)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
    },
  })
}

export function useUnsuppressBenchmark(id: string) {
  const qc = useQueryClient()
  return useMutation<Benchmark, Error, void>({
    mutationFn: () => api.post<Benchmark>(`/admin/benchmarks/${id}/unsuppress`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
      qc.invalidateQueries({ queryKey: ['benchmarks'] })
    },
  })
}

export function useDeleteBenchmark(id: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: () => api.delete<void>(`/admin/benchmarks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
      qc.invalidateQueries({ queryKey: ['benchmarks'] })
    },
  })
}

export interface DraftSummaryInput {
  url?: string
  pdfObjectKey?: string
}

export interface DraftSummaryResult {
  summary: string
  keyMetric: string
  fetchedAs?: 'url' | 'pdf' | 'both'
  extractedChars?: number
}

export function useDraftBenchmarkSummary() {
  return useMutation<DraftSummaryResult, Error, DraftSummaryInput>({
    mutationFn: (body) =>
      api.post<DraftSummaryResult>('/admin/benchmarks/draft-summary', body),
  })
}

// ─── PDF upload / delete (not JSON body — multipart / DELETE) ─────────────────

export interface UploadPdfResult {
  objectKey: string
  size: number
  contentType: string
  downloadUrl: string
}

export function useUploadBenchmarkPdf(id: string) {
  const qc = useQueryClient()
  return useMutation<UploadPdfResult, Error, File>({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('pdf', file)
      const res = await fetch(`${API_BASE_URL}/admin/benchmarks/${id}/pdf`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      })
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        throw new Error(body || `http_${res.status}`)
      }
      return res.json() as Promise<UploadPdfResult>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks', id] })
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
    },
  })
}

export function useDeleteBenchmarkPdf(id: string) {
  const qc = useQueryClient()
  return useMutation<void, Error, void>({
    mutationFn: () => api.delete<void>(`/admin/benchmarks/${id}/pdf`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-benchmarks', id] })
      qc.invalidateQueries({ queryKey: ['admin-benchmarks'] })
    },
  })
}
